/**
 * Ticket descriptions → readable HTML for the description tooltip (ticket grids, Dispatch).
 *
 * Autotask descriptions are almost all plain text from email (checked 2026-10-23: 462 of 464 recent tickets), full
 * of Outlook's plain-text artefacts: `[cid:image001.png@…]` image placeholders, `name@x.com<mailto:name@x.com>` and
 * `text<https://…>` links, `*   ` bullets and runs of blank lines. A few (dmarcian alerts) are real HTML, which is
 * reduced to text first. Every piece of text is HTML-escaped and only tags built here are emitted (p, br, ul/li, hr,
 * a with an http(s)/mailto href), so the result is safe for dangerouslySetInnerHTML without a sanitiser.
 */

const ESC: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ESC[c]);

const REAL_HTML = /<\/?(html|body|p|div|br|span|table|tr|td|b|strong|i|em|ul|ol|li|img|font|h[1-6])[\s>/]/i;
const ENTITIES: Record<string, string> = { nbsp: ' ', amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", '#39': "'" };

/** Real HTML → text with its line structure (DOMParser never runs scripts; style/script bodies are dropped first). */
function htmlToText(html: string): string {
  const prepared = html
    .replace(/<(style|script|head)[\s\S]*?<\/\1>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|tr|li|h[1-6]|table|blockquote)>/gi, '\n')
    .replace(/<li[\s>]/gi, '\n* $&');
  if (typeof DOMParser === 'undefined') return prepared.replace(/<[^>]+>/g, '');
  return new DOMParser().parseFromString(prepared, 'text/html').body.textContent ?? '';
}

/** Outlook's plain-text noise → clean text (links kept as `text\u0000url\u0001` markers for the linker). */
function clean(raw: string): string {
  let s = raw.replace(/\r\n?/g, '\n');
  if (REAL_HTML.test(s)) s = htmlToText(s);
  else s = s.replace(/&(nbsp|amp|lt|gt|quot|apos|#39);/g, (_, e: string) => ENTITIES[e] ?? _);
  return s
    .replace(/\[cid:[^\]]*\]/gi, '')                                  // inline-image placeholders
    .replace(/(\S+)<mailto:([^>\s]+)>/gi, (_, text: string, addr: string) => `\u0000${text}\u0002mailto:${addr}\u0001`)
    .replace(/([^\s<]+)<(https?:\/\/[^>\s]+)>/gi, (_, text: string, url: string) => (text === url ? url : `\u0000${text}\u0002${url}\u0001`))
    .replace(/<(https?:\/\/[^>\s]+)>/gi, '$1')
    .replace(/[ \t]+$/gm, '')
    .replace(/^\?+$/gm, '')                                          // characters Autotask couldn't store (emoji, NBSP)
    .replace(/ /g, ' ')
    .trim();
}

const SAFE_HREF = /^(https?:\/\/|mailto:)/i;
const link = (href: string, text: string) =>
  SAFE_HREF.test(href) ? `<a href="${esc(href)}" title="${esc(href)}" target="_blank" rel="noopener noreferrer">${esc(text)}</a>` : esc(text);

/** A bare URL's label: long ones (signature trackers, deep links) shortened to host + start of path. */
function urlLabel(url: string): string {
  if (url.length <= 60) return url;
  try {
    const u = new URL(url);
    const rest = `${u.pathname}${u.search}`;
    return `${u.host}${rest.length > 24 ? `${rest.slice(0, 24)}…` : rest}`;
  } catch {
    return `${url.slice(0, 57)}…`;
  }
}

/** One line → escaped HTML with links (marked links from clean(), then bare URLs). */
function inline(line: string): string {
  let out = '';
  const re = /\u0000([^\u0002]*)\u0002([^\u0001]*)\u0001|(https?:\/\/[^\s<>"']+[^\s<>"'.,;:!?)\]])/g;
  let last = 0;
  for (let m = re.exec(line); m; m = re.exec(line)) {
    out += esc(line.slice(last, m.index));
    out += m[3] ? link(m[3], urlLabel(m[3])) : link(m[2], m[1]);
    last = m.index + m[0].length;
  }
  return out + esc(line.slice(last));
}

const BULLET = /^\s*(?:[*•·▪◦]|-(?!-))\s+(.*)$/;
const RULE = /^\s*([-_=*])\1{2,}\s*$/;

const cache = new Map<string, string>();

/** Description text → HTML (cached; the same descriptions are hovered repeatedly). */
export function descriptionHtml(text: string): string {
  const hit = cache.get(text);
  if (hit !== undefined) return hit;
  const lines = clean(text).split('\n');
  const blocks: string[] = [];
  let para: string[] = [];
  let list: string[] = [];
  const flushPara = () => { if (para.length) blocks.push(`<p>${para.map(inline).join('<br>')}</p>`); para = []; };
  const flushList = () => { if (list.length) blocks.push(`<ul>${list.map((i) => `<li>${inline(i)}</li>`).join('')}</ul>`); list = []; };
  for (const line of lines) {
    if (!line.trim()) { flushPara(); flushList(); continue; }
    if (RULE.test(line)) { flushPara(); flushList(); blocks.push('<hr>'); continue; }
    const b = BULLET.exec(line);
    if (b) { flushPara(); list.push(b[1]); continue; }
    flushList();
    para.push(line.trim());
  }
  flushPara();
  flushList();
  const html = blocks.join('');
  if (cache.size > 500) cache.delete(cache.keys().next().value!);
  cache.set(text, html);
  return html;
}

/** One-line preview for the grid cell: the same clean-up, links reduced to their text, whitespace collapsed. */
export function descriptionPreview(text: string): string {
  return clean(text).replace(/\u0000([^\u0002]*)\u0002[^\u0001]*\u0001/g, '$1').replace(/\s+/g, ' ').trim();
}
